import { ChannelProtocol, ChannelRole, DescribeSignalingChannelCommand, GetSignalingChannelEndpointCommand, KinesisVideoClient } from '@aws-sdk/client-kinesis-video';
import { GetIceServerConfigCommand, KinesisVideoSignalingClient } from '@aws-sdk/client-kinesis-video-signaling';
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc';
import { openSignalingChannel } from './common.js';
export const fromAwsSecret = (credentials) => ({
    accessKeyId: credentials.AccessKeyId,
    secretAccessKey: credentials.SecretAccessKey,
    sessionToken: credentials.SessionToken
});
export class KinesisSignalingChannel extends SignalingClient {
    iceServers;
    constructor(config, iceServers) {
        super(config);
        this.iceServers = iceServers;
    }
    async getRTCConfiguration() {
        return await Promise.resolve({ iceServers: this.iceServers });
    }
    send(message) {
        throw new Error(`KinesisSignalingChannel does not support sending of custom messages. Tried to send: ${JSON.stringify(message)}`);
    }
    async closeAsync() {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Kinesis signaling may not have closed in time. May be a false-positive and the signaling channel is already closed.')), 5_000);
            this.addListener('close', () => {
                clearTimeout(timer);
                resolve();
            });
            this.close();
        }).finally(() => {
            this.removeAllListeners('close');
        });
    }
}
export async function openRetailKinesisSignaling(props) {
    const { logger, awsSignalingOptions } = props;
    logger.debug('Creating Kinesis signaling client');
    const { channelArn, endpointsByProtocol } = await getChannelArnAndEndpoints(awsSignalingOptions, logger);
    const iceServers = await getIceServers(awsSignalingOptions, { channelArn, endpointsByProtocol }, logger);
    const credentials = await awsSignalingOptions.credentialsProvider();
    const signalingClient = new KinesisSignalingChannel({
        channelARN: channelArn,
        channelEndpoint: endpointsByProtocol['WSS'],
        region: credentials.Region,
        role: Role.VIEWER,
        clientId: awsSignalingOptions.clientId,
        credentials: fromAwsSecret(credentials)
    }, iceServers);
    return openSignalingChannel(signalingClient, logger);
}
function getResourceEndpointForProtocol(endpointList, protocol) {
    const endpoint = endpointList.find((element) => element.Protocol === protocol)?.ResourceEndpoint;
    if (!endpoint) {
        throw new Error(`Kinesis: Did not find an element with protocol=${protocol}`);
    }
    return endpoint;
}
async function getChannelArnForChannelName(kinesisVideoClient, channelName) {
    const describeSignalingChannelCommand = new DescribeSignalingChannelCommand({ ChannelName: channelName });
    const response = await kinesisVideoClient.send(describeSignalingChannelCommand);
    if (!response.ChannelInfo?.ChannelARN) {
        throw new Error(`Kinesis: Could not get channel ARN with channelName=${channelName}, response=${JSON.stringify(response)}`);
    }
    return response.ChannelInfo.ChannelARN;
}
async function getSignalingEndpoints(kinesisVideoClient, channelArn, logger) {
    logger.debug('Kinesis: Requesting signaling channel endpoints');
    const getSignalingChannelEndpointCommand = new GetSignalingChannelEndpointCommand({
        ChannelARN: channelArn,
        SingleMasterChannelEndpointConfiguration: {
            Protocols: [ChannelProtocol.WSS, ChannelProtocol.HTTPS],
            Role: ChannelRole.VIEWER
        }
    });
    const response = await kinesisVideoClient.send(getSignalingChannelEndpointCommand);
    if (!response.ResourceEndpointList) {
        throw new Error(`Kinesis: Could not get signaling channel endpoints for channel ARN=${channelArn}, response=${JSON.stringify(response)}`);
    }
    const endpoints = {
        HTTPS: getResourceEndpointForProtocol(response.ResourceEndpointList, ChannelProtocol.HTTPS),
        WSS: getResourceEndpointForProtocol(response.ResourceEndpointList, ChannelProtocol.WSS)
    };
    logger.debug('Kinesis: Got signaling channel endpoints:', endpoints);
    return endpoints;
}
async function getChannelArnAndEndpoints(config, logger) {
    const credentials = await config.credentialsProvider();
    const kinesisVideoClient = new KinesisVideoClient({
        credentials: fromAwsSecret(credentials),
        region: credentials.Region,
        tls: true
    });
    const channelArn = await getChannelArnForChannelName(kinesisVideoClient, config.channelName);
    const endpointsByProtocol = await getSignalingEndpoints(kinesisVideoClient, channelArn, logger);
    kinesisVideoClient.destroy();
    return { channelArn, endpointsByProtocol };
}
async function getIceServers(config, channelInfo, logger) {
    const credentials = await config.credentialsProvider();
    const { channelArn, endpointsByProtocol } = channelInfo;
    const kinesisVideoSignalingClient = new KinesisVideoSignalingClient({
        credentials: fromAwsSecret(credentials),
        region: credentials.Region,
        tls: true,
        endpoint: endpointsByProtocol[ChannelProtocol.HTTPS]
    });
    logger.debug('Kinesis: Requesting ICE servers');
    const getIceServerConfigCommand = new GetIceServerConfigCommand({
        ClientId: config.clientId,
        ChannelARN: channelArn
    });
    const response = await kinesisVideoSignalingClient.send(getIceServerConfigCommand);
    kinesisVideoSignalingClient.destroy();
    if (!response.IceServerList) {
        throw new Error(`Kinesis: Could not get ICE server config, response=${JSON.stringify(response)}`);
    }
    const stunServerUrl = `stun:stun.kinesisvideo.${credentials.Region}.amazonaws.com:443`;
    const iceServers = [];
    const servers = response.IceServerList[0];
    if (!servers.Uris) {
        throw new Error('Kinesis: iceServer.Uris can not be undefined');
    }
    servers.Uris.push(stunServerUrl);
    iceServers.push({
        urls: servers.Uris,
        username: servers.Username,
        credential: servers.Password
    });
    logger.debug('Kinesis: Got ICE servers:', { iceServers });
    return iceServers;
}
//# sourceMappingURL=retail-kinesis.js.map